import * as ngCore from '@angular/core'
import { ComponentStore } from '@ngrx/component-store'

import type * as models from '../models'

export interface AtnDsSelectSingleState<TValue> {
  config: models.Config
  disabled: boolean
  error?: string | Error
  isOpen: boolean
  items: readonly models.Item[]
  label: string
  loading: boolean
  required: boolean
  placeholder?: string
  searchTerm: string
  selectedValue?: TValue
}

export namespace AtnDsSelectSingleState {
  export const INITIAL: NoMissing<AtnDsSelectSingleState<any>> = {
    config: {
      isSlim: false,
      overlay: {
        maxHeight: 260,
        minSearchCharacters: 2,
      },
    },
    disabled: false,
    error: undefined,
    isOpen: false,
    items: [],
    label: '',
    loading: false,
    placeholder: '',
    required: false,
    searchTerm: '',
    selectedValue: undefined,
  }
}

/**
 * Component-store backing the shared single select trigger and overlay state.
 */
@ngCore.Injectable()
export class AtnDsSelectSingleStore<TValue> extends ComponentStore<
  AtnDsSelectSingleState<TValue>
> {
  /** @see {@link AtnDsSelectSingleState.config} */
  private readonly _configSg = this.selectSignal(state => state.config)
  /** @see {@link AtnDsSelectSingleState.selectedValue} */
  public readonly selectedValueSg = this.selectSignal(
    state => state.selectedValue,
  )
  /** @see {@link AtnDsSelectSingleState.items} */
  public readonly itemsSg = this.selectSignal(state => state.items)
  /** @see {@link AtnDsSelectSingleState.label} */
  public readonly labelSg = this.selectSignal(state => state.label)
  /** @see {@link AtnDsSelectSingleState.loading} */
  public readonly loadingSg = this.selectSignal(state => state.loading)
  /** @see {@link AtnDsSelectSingleState.error} */
  private readonly _errorSg = this.selectSignal(state => state.error)
  /** Computed error message from {@link AtnDsSelectSingleState.error} */
  public readonly errorSg = ngCore.computed((error = this._errorSg()) =>
    error instanceof Error ? error.message : error,
  )
  /** @see {@link AtnDsSelectSingleState.disabled} */
  public readonly disabledSg = this.selectSignal(state => state.disabled)
  /** @see {@link AtnDsSelectSingleState.searchTerm} */
  public readonly searchTermSg = this.selectSignal(state => state.searchTerm)
  /** @see {@link AtnDsSelectSingleState.isOpen} */
  public readonly isOpenSg = this.selectSignal(state => state.isOpen)
  /** @see {@link AtnDsSelectSingleState.required} */
  public readonly requiredSg = this.selectSignal(state => state.required)

  /** @see {@link AtnDsSelectSingleState.placeholder} */
  public readonly placeholderSg = this.selectSignal(state => state.placeholder)

  /** Total count base on {@link AtnDsSelectSingleState.items} length. */
  public readonly countSg = ngCore.computed(
    (items = this.itemsSg()) => items.length,
  )
  /** @see {@link models.Config.isSlim} */
  public readonly isSlimSg = ngCore.computed(
    (config = this._configSg()) => config.isSlim ?? false,
  )
  /** @see {@link models.Config.overlay} */
  private readonly _overlayConfigSg = ngCore.computed(
    (config = this._configSg()) => config.overlay,
  )
  /** @see {@link models.Config.Overlay.hasSearch} */
  public readonly overlayHasSearchSg = ngCore.computed(
    (overlayConfig = this._overlayConfigSg()) =>
      overlayConfig?.hasSearch ?? false,
  )
  /** @see {@link models.Config.Overlay.minSearchCharacters} */
  public readonly overlaySearchMinCharactersSg = ngCore.computed(
    (overlayConfig = this._overlayConfigSg()) =>
      overlayConfig?.minSearchCharacters,
  )
  /** @see {@link models.Config.Overlay.width} */
  public readonly overlayWidthSg = ngCore.computed(
    (overlayConfig = this._overlayConfigSg()) => overlayConfig?.width,
  )
  /** @see {@link models.Config.Overlay.maxHeight} */
  public readonly overlayMaxHeightSg = ngCore.computed(
    (overlayConfig = this._overlayConfigSg()) => overlayConfig?.maxHeight,
  )
  /** @see {@link models.Config.Overlay.entityName} */
  public overlayEntityNameSg = ngCore.computed(
    (overlayConfig = this._overlayConfigSg()) => overlayConfig?.entityName,
  )
  /**
   * Are there visible items based on {@link filteredItemsSg}.
   *
   * @see {@link filteredItemsSg}
   */
  public readonly hasVisibleItemsSg = ngCore.computed(
    () => this.filteredItemsSg()?.length > 0,
  )
  /** Filtered {@link itemsSg} based on {@link searchTermSg}.  */
  public readonly filteredItemsSg = ngCore.computed(
    (searchTerm = this.searchTermSg(), items = this.itemsSg()) =>
      searchTerm.length
        ? items.filter(item =>
            AtnDsSelectSingleStore.filterPredicate(item, searchTerm),
          )
        : items,
  )
  /**
   * Index of the selected item in {@link itemsSg}.
   *
   * TODO: elaborate documentation that we are using the value or label to
   * determine the selected item.
   */
  public readonly selectedIdxSg = ngCore.computed((items = this.itemsSg()) =>
    items.findIndex(item =>
      item.value !== undefined
        ? item.value === this.selectedValueSg()
        : this.selectedValueSg() === undefined || item.label === this.selectedValueSg(),
    ),
  )
  /**
   * Selected item based on {@link selectedIdxSg}.
   */
  public readonly selectedItemSg = ngCore.computed(
    (items = this.itemsSg(), selectedIdx = this.selectedIdxSg()) =>
      items[selectedIdx],
  )

  constructor() {
    super(AtnDsSelectSingleState.INITIAL)
  }

  /** TODO:  */
  public setSearchValue(searchTerm: string): void {
    if (
      searchTerm.length > (this.overlaySearchMinCharactersSg() ?? -1) ||
      searchTerm.length === 0
    ) {
      this.patchState({ searchTerm })
    }
  }
}
export namespace AtnDsSelectSingleStore {
  /**
   * Default case-insensitive label filter used by the overlay search box.
   */
  export function filterPredicate(
    item: models.Item,
    searchTerm: string,
  ): boolean {
    return item.label
      .trim()
      .toLowerCase()
      .includes(searchTerm.trim().toLowerCase())
  }
}
